import {Component, inject, signal, DestroyRef} from '@angular/core';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {ToastrService} from 'ngx-toastr';
import {RuleCheckerService} from '../../../../services/store/rule-checker.service';
import {ModalComponent} from '../../../../shared/components/modal/modal.component';
import {StoreSpecialWorkingTimeService} from '../../../../services/http/store/store-special-working-time.service';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {SHARED_IMPORTS} from "../../../../shared/standalone-imports";

/**
 * Defines all the data the delete modal needs.
 */
export interface SpecificWorkingTimesDeleteModalData {
  storeId?: string;
  date: string;
}

/**
 * Handles deleting a special working time.
 */
@Component({
  selector: 'app-specific-working-times-delete',
  templateUrl: './specific-working-times-delete.component.html',
  styleUrls: ['./specific-working-times-delete.component.less'],
  imports: [SHARED_IMPORTS]
})
export class SpecificWorkingTimesDeleteComponent {
  private storeSpecialWorkingTimeService = inject(StoreSpecialWorkingTimeService);
  private toastr = inject(ToastrService);
  private ruleCheckerService = inject(RuleCheckerService);
  private destroyRef = inject(DestroyRef);
  private destroy$ = new Subject<void>();

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroy$.next();
      this.destroy$.complete();
    });
  }


  /** An identifier of a store. */
  readonly storeId = signal<string | undefined>(undefined);
  /** A date at which a special working time should be deleted. */
  readonly date = signal<string>('');

  /** `True` if a request sending to backend is in progress. */
  readonly loading = signal(false);

  static open(modalService: NgbModal, data: SpecificWorkingTimesDeleteModalData): NgbModalRef {
    const modalRef = modalService.open(SpecificWorkingTimesDeleteComponent);
    const instance = modalRef.componentInstance as SpecificWorkingTimesDeleteComponent;
    instance.storeId.set(data.storeId);
    instance.date.set(data.date);
    return modalRef;
  }

  /**
   * Sends request to backend to delete a special store working time.
   * @param modalComponent a modal component of special working time form.
   */
  confirm(modalComponent: ModalComponent): void {
    this.loading.set(true);

    this.storeSpecialWorkingTimeService.deleteSpecialStoreWorkingTime(this.storeId(), this.date())
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                modalComponent.activeModal.close();
                this.loading.set(false);
                this.toastr.success('Specific store trading time has been successfully deleted');
                this.ruleCheckerService.check();
              },
              error: error => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              }
            });
  }
}